import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import type { Prompt, PromptVariable, UpdatePromptInput } from '@/domain/prompt.types'
import { lintPrompt } from '@/domain/prompt.types'
import { useDeletePrompt, usePromptEvalGate, usePublishPrompt, useUpdatePrompt } from '@/hooks/usePrompts'
import { notification } from '@/notifications/notification-store'

export interface PromptEditForm {
	name: string
	description: string
	body: string
	variables: PromptVariable[]
}

const formOf = (prompt: Prompt): PromptEditForm => ({
	name: prompt.name,
	description: prompt.description ?? '',
	body: prompt.body,
	variables: prompt.variables,
})

/** Only the fields that actually changed, so an untouched field is never sent. */
const toPatch = (form: PromptEditForm, original: Prompt): UpdatePromptInput => {
	const patch: UpdatePromptInput = {}
	if (form.name !== original.name) patch.name = form.name
	if (form.description !== (original.description ?? '')) patch.description = form.description
	if (form.body !== original.body) patch.body = form.body
	if (JSON.stringify(form.variables) !== JSON.stringify(original.variables)) patch.variables = form.variables
	return patch
}

/**
 * Owns every piece of mutable editor state, so the page component stays a view.
 *
 * Save is BLOCKED while the body references an undeclared variable: the backend
 * refuses that save anyway, so catching it here turns a server error into inline
 * feedback. An unused variable only warns — it is confusing, not broken.
 */
export function usePromptEditor(prompt: Prompt) {
	const [editing, setEditing] = useState(false)
	const [form, setForm] = useState<PromptEditForm | null>(null)
	const update = useUpdatePrompt()
	const publishFlow = usePublishFlow(prompt.id)

	const current = form ?? formOf(prompt)
	const lint = useMemo(() => lintPrompt(current.body, current.variables), [current.body, current.variables])
	const canSave = lint.undeclared.length === 0

	const startEdit = () => {
		setForm(formOf(prompt))
		setEditing(true)
	}

	const cancelEdit = () => {
		setForm(null)
		setEditing(false)
		update.reset()
	}

	const save = () => {
		if (!canSave) return
		const patch = toPatch(current, prompt)

		// Nothing changed — treat Save as Done rather than sending an empty PUT.
		if (Object.keys(patch).length === 0) {
			cancelEdit()
			return
		}

		update.mutate(
			{ id: prompt.id, patch },
			{
				onSuccess: () => {
					setForm(null)
					setEditing(false)
				},
				// Deliberately keeps edit mode and the user's input on failure, so a
				// transient error does not discard their work.
			},
		)
	}

	return {
		editing,
		form: current,
		setForm: (next: PromptEditForm) => setForm(next),
		lint,
		canSave,
		startEdit,
		cancelEdit,
		save,
		/**
		 * Persist the model config or the output schema on their own.
		 *
		 * Separate from `save` because those live behind their own Edit buttons and are
		 * not part of the body/variables draft — routing them through the same diff
		 * would send the whole form every time someone changed a temperature.
		 */
		saveSettings: (patch: UpdatePromptInput) => update.mutate({ id: prompt.id, patch }),
		isSaving: update.isPending,
		saveFailed: update.isError,
		...publishFlow,
	}
}

/**
 * The delete flow: a confirmation, the mutation, and where to go afterwards.
 *
 * Separate from `usePromptEditor` because it is a different concern — editing a draft
 * versus removing the prompt entirely — and because a delete NAVIGATES on success,
 * which nothing else in the editor does.
 */
export function usePromptDeletion(prompt: Prompt) {
	const [confirming, setConfirming] = useState(false)
	const remove = useDeletePrompt()
	const navigate = useNavigate()

	return {
		confirming,
		open: () => {
			remove.reset()
			setConfirming(true)
		},
		cancel: () => {
			remove.reset()
			setConfirming(false)
		},
		confirm: () =>
			remove.mutate(prompt.id, {
				onSuccess: () => {
					notification.success(`Deleted "${prompt.name}"`)
					void navigate('/prompts')
				},
				// Reported through the status bar AND left in the dialog: the dialog stays
				// open so the reason sits next to the control that failed.
				onError: (error) => notification.error(extractApiErrorMessage(error, 'The prompt could not be deleted.')),
			}),
		isDeleting: remove.isPending,
		error: remove.isError ? extractApiErrorMessage(remove.error, 'The prompt could not be deleted.') : null,
	}
}

/**
 * The two-step publish: open a dialog showing the eval result, then confirm.
 *
 * Separate from the editor's own state because it is a different concern — editing a
 * draft versus repointing what production traffic resolves to.
 */
function usePublishFlow(promptId: string) {
	const [publishOpen, setPublishOpen] = useState(false)
	const publish = usePublishPrompt()
	// Fetched only while the dialog is open — see `usePromptEvalGate`.
	const evalGate = usePromptEvalGate(promptId, publishOpen)

	return {
		publishOpen,
		openPublish: () => {
			publish.reset()
			setPublishOpen(true)
		},
		cancelPublish: () => setPublishOpen(false),
		confirmPublish: () =>
			publish.mutate(promptId, {
				// Closed only on success: a refusal by the tenant's gate has to stay visible
				// in the dialog that asked for it, or it reads as nothing having happened.
				onSuccess: () => setPublishOpen(false),
			}),
		evalGate: evalGate.data,
		// `isFetching`, not `isPending`. `isPending` is false whenever cached data exists, so a
		// reopened dialog showed a previous verdict with no loading state while the refetch was
		// still in flight — presenting a stale answer as the current one.
		isLoadingEvalGate: evalGate.isFetching && publishOpen,
		isPublishing: publish.isPending,
		publishFailed: publish.isError,
		publishError: publish.error,
	}
}
